import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import {
   Button,
   colors,
   Hierarchy,
   SubVariant
} from '@flowwai-frontend/design-system'
import { PlusIcon, XCloseIcon } from '@flowwai-frontend/design-system/icons'
import { LocationValue } from '@flowwai-frontend/shared/common-components'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../types'
import { getCoordinatesForGeoShape } from '../../utils/GeoFieldsUtil'
import CoordinateInput from '../MapBoxEditableMap/CoordinateInput'

interface Props {
   isReadOnly: boolean
   geoShape: LocationValue
   validateAndUpdateGeoShape: (geoShape: LocationValue) => void
   onAddCoordinates: (clickedCoordinates: MapCoordinates) => void
   onMoveMarker: (coordinates: MapCoordinates, index: number) => void
   onDeleteCoordinates: (index: number) => void
   containerClassName?: string
   listContainerClassName?: string
}

const CoordinatesList = (props: Props): React.ReactElement => {
   const { isReadOnly } = props

   const [coordinates, setCoordinates] = useState<MapCoordinates[]>(
      getCoordinatesForGeoShape(props.geoShape)
   )
   const [
      dummyCoordinate,
      setDummyCoordinate
   ] = useState<MapCoordinates | null>(null)

   const [showDummyCoordinate, setShowDummyCoordinate] = useState<boolean>(
      !coordinates.length && !isReadOnly
   )

   useEffect(() => {
      const newCoordinates = getCoordinatesForGeoShape(props.geoShape)
      setCoordinates(newCoordinates)
      const hasCoordinates = newCoordinates.length > 0
      const shouldResetDummyCoordinates =
         props.geoShape.type === 'POINT' && hasCoordinates

      if (shouldResetDummyCoordinates) {
         setShowDummyCoordinate(false)
         setDummyCoordinate(null)
      }
   }, [JSON.stringify(props.geoShape.value)])

   const { mapsT, showCRMSuccessToast } = useMapsUtilityContext()

   const isMobileDevice = isMobile()

   const shouldRenderCoordinatesList =
      showDummyCoordinate && !isReadOnly ? true : coordinates.length > 0

   const shouldRenderDummyCoordinate = showDummyCoordinate && !isReadOnly

   const disableAddCoordinatesButton =
      showDummyCoordinate ||
      (props.geoShape.type === 'POINT' && coordinates.length >= 1)

   const onUpdateCoordinates = (coordinates: MapCoordinates[]): void => {
      switch (props.geoShape.type) {
         case 'POINT': {
            props.validateAndUpdateGeoShape({
               type: 'POINT',
               value: coordinates.at(0)
            })
            break
         }
         default: {
            props.validateAndUpdateGeoShape({
               type: props.geoShape.type,
               value: coordinates
            })
            break
         }
      }
   }

   const onChangeCoordinates = (value: MapCoordinates, index: number): void => {
      const isDummyCoordinate = index === coordinates.length

      if (isDummyCoordinate) {
         setDummyCoordinate(null)
         setShowDummyCoordinate(false)
         props.onAddCoordinates(value)
      } else {
         props.onMoveMarker(value, index)
      }
   }

   const onClear = (): void => {
      onUpdateCoordinates([])
   }

   const onDeleteCoordinate = (index: number): void => {
      const isDummyCoordinate = index === coordinates.length

      if (isDummyCoordinate) {
         setDummyCoordinate(null)
         setShowDummyCoordinate(false)
         return
      }

      props.onDeleteCoordinates(index)
   }

   const renderAddCoordinateButton = (): React.ReactElement => (
      <Button
         hierarchy={Hierarchy.Link}
         subVariant={SubVariant.Primary}
         isDisabled={disableAddCoordinatesButton}
         leftIcon={({ stroke }) => <PlusIcon fill={stroke} />}
         onClick={() => setShowDummyCoordinate(true)}
         size='Small'
         className='self-start'
      >
         {mapsT('addCoordinate')}
      </Button>
   )

   const renderClearButton = (): React.ReactNode => (
      <Button
         size='Small'
         hierarchy={Hierarchy.Link}
         subVariant={SubVariant.Destructive}
         leftIcon={() => (
            <XCloseIcon
               width={18}
               height={18}
               fill={colors['error-600']}
               className='-mr-1'
            />
         )}
         onClick={onClear}
         className={'!text-error-600'}
      >
         {mapsT('clear')}
      </Button>
   )

   const renderCoordinatesList = (): React.ReactElement => (
      <div className={cn('flex flex-col gap-3', props.listContainerClassName)}>
         {coordinates.map((coordinate, index) => (
            <CoordinateInput
               key={index}
               value={coordinate}
               onChangeValue={value => onChangeCoordinates(value, index)}
               onDelete={() => onDeleteCoordinate(index)}
               position={index + 1}
               isReadOnly={isReadOnly}
               mapsT={mapsT}
               isMobile={isMobileDevice}
               showCRMSuccessToast={showCRMSuccessToast}
            />
         ))}

         {shouldRenderDummyCoordinate ? (
            <CoordinateInput
               key='dummy-coordinate'
               value={dummyCoordinate}
               onChangeValue={value =>
                  onChangeCoordinates(value, coordinates.length)
               }
               onDelete={() => onDeleteCoordinate(coordinates.length)}
               position={coordinates.length + 1}
               isReadOnly={isReadOnly}
               mapsT={mapsT}
               isMobile={isMobileDevice}
            />
         ) : null}
      </div>
   )

   return (
      <div className={cn('flex flex-col gap-4', props.containerClassName)}>
         {shouldRenderCoordinatesList ? renderCoordinatesList() : null}

         {isReadOnly ? null : (
            <div className={cn('flex items-center justify-between')}>
               {renderAddCoordinateButton()}
               {coordinates.length ? renderClearButton() : null}
            </div>
         )}
      </div>
   )
}

export default observer(CoordinatesList)
